import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../partials/link/TextLink';
import { useTheme } from '../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { H4, semanticCSS } from '../../../typography/semanticTags';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

const componentConfigurationKey = 'sectionHeader';
const createStyles = (baseUnit: BaseUnit, titleColor: string, marginTop: number) => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: `${baseUnit * marginTop}px`,
    marginBottom: `${baseUnit * 0.5}px`,
  },
  titleItem: {
    flex: '1 1 auto',
    color: titleColor,
  },
});

const createLinkTextStyles = () => StyleSheet.create({
  linkText: {

    [MEDIA_BREAKPOINTS.large]: {
      ':hover': {
        textDecoration: 'underline',
      },
    },
    [MEDIA_BREAKPOINTS.medium]: {
      ':hover': {
        color: 'initial',
      },
    },
    [MEDIA_BREAKPOINTS.small]: {
      ':hover': {
        color: 'initial',
      },
    },

  },
});

const createLink = ({ linkText,
  idleColor,
  activeStateColor,
  hoverStateColor,
  href,
}: SectionHeaderDataProps & ThemedLinkStyle) => {
  if (href && linkText) {
    const linkStyle = generateLinkStyleFromTheme({
      idleColor,
      activeStateColor,
      hoverStateColor,
    });
    const style = createLinkTextStyles();

    return (
      <TextLinkComponent href={href} linkStyle={linkStyle}>
        <H4 styles={semanticCSS(style.linkText)}>{linkText}</H4>
      </TextLinkComponent>
    );
  }
  return null;
};

interface SectionHeaderComponentThemeProps {
  baseUnit: BaseUnit;
  titleColor: string;
  marginTop?: number;
}

interface SectionHeaderDataProps {
  linkText: string | null;
  href: string | null
}


type SectionHeaderComponentProps = SectionHeaderComponentThemeProps & SectionHeaderDataProps & ThemedLinkStyle;

export const SectionHeaderComponent: React.FunctionComponent<SectionHeaderComponentProps> = ({
  linkText,
  baseUnit,
  titleColor,
  idleColor = '',
  activeStateColor = '',
  hoverStateColor = '',
  href,
  marginTop = 1.5,
  children,
}) => {
  const style = createStyles(baseUnit, titleColor, marginTop);

  return (
    <div className={css(style.container)}>
      <div className={css(style.titleItem)}>
        {children}
      </div>
      {createLink({
        linkText,
        idleColor,
        activeStateColor,
        hoverStateColor,
        href,
      })}
    </div>
  );
};

const mapThemeProps = (theme: SectionHeaderComponentThemeProps & ThemedLinkStyle) => ({
  baseUnit: theme.baseUnit,
  titleColor: theme.titleColor,
  idleColor: theme.idleColor,
  activeStateColor: theme.activeStateColor,
  hoverStateColor: theme.hoverStateColor,
});

export const SectionHeader: React.FunctionComponent<SectionHeaderDataProps> = props => {
  const themeContext = useTheme(mapThemeProps, componentConfigurationKey);
  return <SectionHeaderComponent {...themeContext} {...props} />;
};
